import * as RToast from '@radix-ui/react-toast';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from './cn';

type ToastTone = 'info' | 'success' | 'warning' | 'error';

interface ToastItem {
  id: number;
  title: string;
  description?: ReactNode;
  tone: ToastTone;
  /** request_id surfaced for support correlation (spec §5.1 / §17). */
  requestId?: string;
}

interface ToastApi {
  toast: (t: Omit<ToastItem, 'id'>) => void;
  success: (title: string, description?: ReactNode) => void;
  error: (title: string, opts?: { description?: ReactNode; requestId?: string }) => void;
  info: (title: string, description?: ReactNode) => void;
  warning: (title: string, description?: ReactNode) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const toneClass: Record<ToastTone, string> = {
  info: 'border-accent-blue',
  success: 'border-signal-teal',
  warning: 'border-signal-amber',
  error: 'border-signal-error',
};

let seq = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [items, setItems] = useState<ToastItem[]>([]);

  const push = useCallback((t: Omit<ToastItem, 'id'>) => {
    setItems((prev) => [...prev, { ...t, id: ++seq }]);
  }, []);

  // Stable for the provider's lifetime: a fresh object per render would
  // re-render every consumer on each toast and churn every callback built on
  // it (useApiError), re-firing any effect that lists one as a dependency.
  const api = useMemo<ToastApi>(
    () => ({
      toast: push,
      success: (title, description) => push({ title, description, tone: 'success' }),
      error: (title, opts) =>
        push({ title, description: opts?.description, tone: 'error', requestId: opts?.requestId }),
      info: (title, description) => push({ title, description, tone: 'info' }),
      warning: (title, description) => push({ title, description, tone: 'warning' }),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      <RToast.Provider swipeDirection="right" duration={6000}>
        {children}
        {items.map((item) => (
          <RToast.Root
            key={item.id}
            onOpenChange={(open) => {
              if (!open) setItems((prev) => prev.filter((x) => x.id !== item.id));
            }}
            className={cn(
              'data-[state=open]:animate-in data-[state=closed]:animate-out flex flex-col gap-[2px] rounded-md border-s-[3px] border-[0.5px] border-hairline bg-canvas-paper p-md shadow-nav-scrolled',
              toneClass[item.tone],
            )}
          >
            <RToast.Title className="text-small font-medium text-ink-deep">{item.title}</RToast.Title>
            {item.description && (
              <RToast.Description className="text-small text-ink-mid">
                {item.description}
              </RToast.Description>
            )}
            {item.requestId && (
              <details className="mt-2xs">
                <summary className="cursor-pointer text-label text-ink-mute">{t('ui.reportThis')}</summary>
                <code className="tnum mt-2xs block select-all font-mono text-label text-ink-mute">
                  {item.requestId}
                </code>
              </details>
            )}
          </RToast.Root>
        ))}
        <RToast.Viewport className="fixed bottom-0 end-0 z-[100] flex w-[380px] max-w-[100vw] flex-col gap-sm p-lg" />
      </RToast.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
}
